/**
 * Rule Configuration Models
 * Type-safe models for all 6 rule types and their configurations
 */

// ============================================================================
// Enums
// ============================================================================

export enum RuleType {
  DateValidation = 'DateValidation',
  CellValueCheck = 'CellValueCheck',
  CellColorCheck = 'CellColorCheck',
  FormulaValidation = 'FormulaValidation',
  PercentageSumRule = 'PercentageSumRule',
  CrossSheetAggregation = 'CrossSheetAggregation'
}

export enum DateCalculator {
  PreviousFriday = 'PreviousFriday',
  LastNWeekEndings = 'LastNWeekEndings',
  Custom = 'Custom'
}

export enum CellValueConditionType {
  NotBlank = 'NotBlank',
  IsBlank = 'IsBlank',
  GreaterThan = 'GreaterThan',
  LessThan = 'LessThan',
  GreaterThanOrEqual = 'GreaterThanOrEqual',
  LessThanOrEqual = 'LessThanOrEqual',
  Equals = 'Equals',
  NotEquals = 'NotEquals',
  Between = 'Between',
  NoRefErrors = 'NoRefErrors'
}

export enum LogicalOperator {
  AND = 'AND',
  OR = 'OR'
}

export enum CellColor {
  Green = 'green',
  Amber = 'amber',
  Red = 'red'
}

export enum ThresholdOperator {
  GreaterThan = '>',
  GreaterThanOrEqual = '>=',
  LessThan = '<',
  LessThanOrEqual = '<=',
  Equals = '='
}

// ============================================================================
// Date Validation Configuration
// ============================================================================

export interface DateValidationConfig {
  dateCalculator: DateCalculator;
  weekCount?: number; // For LastNWeekEndings
  cells: string[]; // e.g., ["B1", "B2"] or ["E13", "G13", "I13", "K13", "M13", "O13"]
  format?: string; // e.g., "dd/MM/yyyy"
}

// ============================================================================
// Cell Value Check Configuration (Enhanced)
// ============================================================================

export interface CellValueCondition {
  type: CellValueConditionType;
  value?: any; // For comparisons
  min?: number; // For Between
  max?: number; // For Between
}

export interface CellValueCheckConfig {
  range?: string; // e.g., "E15:O15"
  cells?: string[]; // Alternative to range
  columnFilter?: string[]; // e.g., ["E", "G", "I", "K", "M", "O"] - filters columns in range
  conditions: CellValueCondition[];
  operator?: LogicalOperator; // For compound conditions (default: AND)
}

// ============================================================================
// Cell Color Check Configuration (Enhanced)
// ============================================================================

export interface ColorThreshold {
  value: number; // Threshold value or percentage
  operator: ThresholdOperator;
  color: CellColor;
}

// Two-tier (target-based)
export interface TwoTierColorConfig {
  tier: '2-tier';
  valueCells: string[]; // Cells to check (e.g., ["E16"])
  targetCell: string; // Cell containing target (e.g., "C16")
  thresholds: ColorThreshold[]; // Usually 2: green when >= target, red when < target
}

// Three-tier (fixed thresholds)
export interface ThreeTierColorConfig {
  tier: '3-tier';
  cells: string[]; // Cells to check
  thresholds: ColorThreshold[]; // Usually 3: green >= 70%, amber >= 60%, red < 60%
}

export type CellColorCheckConfig = TwoTierColorConfig | ThreeTierColorConfig;

// ============================================================================
// Formula Validation Configuration
// ============================================================================

export interface FormulaValidationConfig {
  sumRange: string; // e.g., "D14:T14"
  resultCell: string; // e.g., "U14"
  tolerance?: number; // Acceptable difference (default: 0.01)
}

// ============================================================================
// Percentage Sum Rule Configuration
// ============================================================================

export interface PercentageSumRuleConfig {
  range: string; // e.g., "E172:E183"
  expectedSum: number; // Usually 100
  tolerance?: number; // Acceptable difference (default: 0.001)
  validateIndividualCells?: boolean; // Also check 0-100% for each cell
}

// ============================================================================
// Cross-Sheet Aggregation Configuration
// ============================================================================

export interface SheetCellSource {
  sheet: string; // e.g., "Connells"
  cell: string; // e.g., "E16"
}

export interface CrossSheetAggregationConfig {
  resultSheet: string; // e.g., "NatWest Overall"
  resultCell: string; // e.g., "E14"
  sources: SheetCellSource[]; // Array of source sheet/cell pairs
  formula?: string; // Optional: parsed Excel formula
  operation?: 'Sum' | 'Average' | 'Min' | 'Max'; // Default: Sum
}

// ============================================================================
// Union Type for All Configurations
// ============================================================================

export type RuleConfiguration =
  | DateValidationConfig
  | CellValueCheckConfig
  | CellColorCheckConfig
  | FormulaValidationConfig
  | PercentageSumRuleConfig
  | CrossSheetAggregationConfig;

// ============================================================================
// Rule Category & Template Models
// ============================================================================

export interface RuleCategory {
  id: string;
  name: string;
  displayOrder: number;
  description?: string;
  isActive: boolean;
}

export interface RuleTemplate {
  id: string;
  name: string;
  description: string;
  ruleType: RuleType;
  categoryId?: string;
  category?: RuleCategory;
  templateConfiguration: RuleConfiguration;
  isActive: boolean;
}

// ============================================================================
// Wizard State Model
// ============================================================================

export interface RuleBuilderWizardState {
  // Step 1: Rule Type Selection
  selectedRuleType: RuleType | null;
  selectedTemplateId: string | null;

  // Step 2: Category & Basic Info
  categoryId: string | null;
  ruleName: string;
  ruleDescription: string;
  targetSheets: string[];

  // Step 3: Configuration (dynamic based on rule type)
  configuration: RuleConfiguration | null;

  // Step 4: Test Results
  testFileUploaded: boolean;
  testResults: {
    passed: boolean;
    affectedCells?: string[];
    failureReason?: string;
  } | null;

  // Step 5: Review
  reviewed: boolean;

  // Step 6: Save Options
  activateImmediately: boolean;
  saveAsTemplate: boolean;

  // Metadata
  currentStep: number;
  isEditMode: boolean;
  editRuleId: string | null;
  isDirty: boolean;
}

// ============================================================================
// Rule Type Metadata (for UI display)
// ============================================================================

export interface RuleTypeInfo {
  type: RuleType;
  label: string;
  description: string;
  icon: string; // Material icon name
  complexity: 'Simple' | 'Moderate' | 'Complex';
  examples: string[];
}

export const RULE_TYPE_METADATA: Record<RuleType, RuleTypeInfo> = {
  [RuleType.DateValidation]: {
    type: RuleType.DateValidation,
    label: 'Date Validation',
    description: 'Validate dates match expected patterns (previous Friday, last N week endings)',
    icon: 'event',
    complexity: 'Simple',
    examples: [
      'End of Week = Previous Friday',
      'Last 6 Week Ending dates in header row'
    ]
  },
  [RuleType.CellValueCheck]: {
    type: RuleType.CellValueCheck,
    label: 'Cell Value Check',
    description: 'Validate cell values with conditions (not blank, greater than, between, etc.)',
    icon: 'fact_check',
    complexity: 'Moderate',
    examples: [
      'No #REF errors in range A13:U200',
      'Values in E15, G15, I15 are not blank AND > 1',
      'Cell E14 is between 0 and 100'
    ]
  },
  [RuleType.CellColorCheck]: {
    type: RuleType.CellColorCheck,
    label: 'Cell Color Check',
    description: 'Ensure cell background colors match SLA thresholds',
    icon: 'palette',
    complexity: 'Moderate',
    examples: [
      'E16 >= C16 → green circle, < C16 → red circle',
      'E36 >= 70% → green, >= 60% → amber, < 60% → red'
    ]
  },
  [RuleType.FormulaValidation]: {
    type: RuleType.FormulaValidation,
    label: 'Formula Validation',
    description: 'Verify sum or formula results match expected values',
    icon: 'functions',
    complexity: 'Simple',
    examples: [
      'SUM(D14:T14) equals U14',
      'Total row matches sum of detail rows'
    ]
  },
  [RuleType.PercentageSumRule]: {
    type: RuleType.PercentageSumRule,
    label: 'Percentage Sum (100%)',
    description: 'Validate range sums to 100% with individual cell 0-100% check',
    icon: 'percent',
    complexity: 'Simple',
    examples: [
      'SUM(E172:E183) = 100%',
      'Each cell in range is 0-100%'
    ]
  },
  [RuleType.CrossSheetAggregation]: {
    type: RuleType.CrossSheetAggregation,
    label: 'Cross-Sheet Aggregation',
    description: 'Validate values match sum/average across multiple sheets',
    icon: 'library_books',
    complexity: 'Complex',
    examples: [
      'NatWest Overall E14 = Connells!E16 + esurv!E16 + Countrywide!E16',
      'Paste Excel formula: =Sheet1!A1+Sheet2!A1'
    ]
  }
};
